import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Leaf } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/blog", label: "Blog" },
  { to: "/negozio", label: "Negozio" },
  { to: "/attrezzi", label: "Attrezzi" },
  { to: "/lavori", label: "Lavori" },
  { to: "/contatti", label: "Contatti" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setOpen } = useCart();
  const location = useLocation();
  const onDark = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        onDark ? "bg-transparent text-bone" : "border-b border-line bg-bone/70 text-forest backdrop-blur-xl"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Leaf className="h-5 w-5 text-terra" strokeWidth={1.5} />
          <span className="font-serif text-xl tracking-tight">
            Luca Mannu <span className="italic">Giardini</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline font-accent text-xs uppercase tracking-[0.2em] transition-opacity duration-300 ${
                  isActive ? "text-terra" : "opacity-80 hover:opacity-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-open-button"
            className="relative flex items-center gap-2 rounded-full border border-current px-4 py-2 font-accent text-xs uppercase tracking-[0.2em] transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden lg:inline">Carrello</span>
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-terra text-[10px] font-bold text-bone" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
        </nav>

        <div className="flex items-center gap-3 md:hidden">
          <button onClick={() => setOpen(true)} className="relative p-2" data-testid="cart-open-button-mobile" aria-label="Carrello">
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-terra text-[9px] font-bold text-bone">{count}</span>
            )}
          </button>
          <button onClick={() => setMenuOpen((v) => !v)} className="p-2" data-testid="mobile-menu-button" aria-label="Menu">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-line bg-bone text-forest md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className="py-3 font-accent text-sm uppercase tracking-[0.2em]" data-testid={`nav-mobile-${l.label.toLowerCase()}`}>
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
